// Types
import { Dimension } from '@supermeme-ai/types'
import { isMobile } from 'react-device-detect'

/**
 * Calculates responsive dimensions for memes based on:
 * - Original dimensions stored in the database
 * - Current device's max dimension
 * - Original creation device type (mobile/desktop)
 *
 * This ensures consistent display across all devices while maintaining aspect ratio.
 *
 * @param originalWidth Width stored in the database
 * @param originalHeight Height stored in the database
 * @param currentMaxDimension Max dimension for the current device
 * @param desktopMaxDimension Standard desktop max dimension (default: 500)
 * @returns Responsive dimensions that maintain aspect ratio
 */
export const getResponsiveMemeSize = (
  originalWidth: number,
  originalHeight: number,
  currentMaxDimension: number,
  desktopMaxDimension: number = 500 // Default desktop max dimension
): Dimension => {
  // Calculate aspect ratio of the original image
  const aspectRatio = originalHeight / originalWidth

  // Detect if we're currently on a mobile device
  const currentlyOnMobile = isMobile

  // Detect if the image was likely created on mobile
  // Images created on mobile typically have width > desktopMaxDimension or width exactly matching a common mobile width
  const likelyCreatedOnMobile =
    originalWidth > desktopMaxDimension ||
    (originalWidth < desktopMaxDimension &&
      [320, 360, 375, 390, 393, 412, 414, 428].includes(originalWidth))

  let targetWidth: number

  // CASE 1: Created on mobile, viewing on desktop
  // Need to scale up appropriately to look good on desktop
  if (likelyCreatedOnMobile && !currentlyOnMobile) {
    // Scale up to desktop size, but maintain aspect ratio
    targetWidth = Math.min(
      desktopMaxDimension,
      originalWidth * (desktopMaxDimension / originalWidth)
    )
  }
  // CASE 2: Created on desktop, viewing on mobile
  // Need to scale down to fit mobile screen
  else if (!likelyCreatedOnMobile && currentlyOnMobile) {
    // Scale down to fit mobile screen
    targetWidth = Math.min(currentMaxDimension, originalWidth)
  }
  // CASE 3: Created on mobile, viewing on mobile OR Created on desktop, viewing on desktop
  // Use the appropriate max dimension for the current device
  else {
    targetWidth = Math.min(currentMaxDimension, originalWidth)
  }

  // Calculate height based on the aspect ratio
  const targetHeight = targetWidth * aspectRatio

  return {
    width: Math.round(targetWidth),
    height: Math.round(targetHeight),
  }
}
